import { createContext, ReactNode, useContext, useEffect, useReducer } from 'react';
import produce from 'immer';

import { useGarmParams } from '@insights_module/hooks/useGarmParams';
import { useViews } from '@insights_module/hooks/useViews';
import { useIABParams } from '@insights_module/hooks/useIABParams';
import { useAppleParams } from '@insights_module/hooks/useAppleParams';
import { useGroupParams } from '@insights_module/hooks/useGroupParams';
import { INSIGHTS_QUERY_KEY } from '@insights_module/constants/queryKeys';
import { objectToToken, unimplemented } from '@utils/commonUtils';
import { useInsightsParams } from '@insights_module/hooks/useInsightsParams';

type ViewsContextKeys =
  | 'insights.views.show_views_drawer'
  | 'insights.views.show_create_view_modal'
  | 'insights.views.view_to_be_deleted'
  | 'insights.views.view_to_be_renamed'
  | 'insights.views.view_to_be_edited'
  | 'insights.views.selected_view'
  | 'insights.views.isViewChanged';

const VIEWS_CONTEXT_KEYS = {
  SHOW_VIEWS_DRAWER: 'insights.views.show_views_drawer' as ViewsContextKeys,
  SHOW_CREATE_VIEW_MODAL: 'insights.views.show_create_view_modal' as ViewsContextKeys,
  VIEW_TO_BE_DELETED: 'insights.views.view_to_be_deleted' as ViewsContextKeys,
  VIEW_TO_BE_RENAMED: 'insights.views.view_to_be_renamed' as ViewsContextKeys,
  VIEW_TO_BE_EDITED: 'insights.views.view_to_be_edited' as ViewsContextKeys,
  SELECTED_VIEW: 'insights.views.selected_view' as ViewsContextKeys,
  IS_VIEW_CHANGED: 'insights.views.isViewChanged' as ViewsContextKeys,
};

type Action = { type: ViewsContextKeys; payload: string | boolean | null };

export interface IViewsContext {
  showViewsDrawer: boolean;
  showCreateViewModal: boolean;
  selectedView: string | null;
  viewToBeDeleted: string | null;
  viewToBeRenamed: string | null;
  viewToBeEdited: string | null;
  isViewChanged: boolean;
  setShowViewsDrawer: (value: boolean | ((prevState: boolean) => boolean)) => void;
  setShowCreateViewModal: (value: boolean) => void;
  setViewToBeDeleted: (value: string | null) => void;
  setViewToBeRenamed: (value: string | null) => void;
  setViewToBeEdited: (value: string | null) => void;
  setSelectedView: (value: string | null) => void;
  setIsViewChanged: (value: boolean) => void;
}

const initialState: IViewsContext = {
  showViewsDrawer: false,
  showCreateViewModal: false,
  selectedView: null,
  viewToBeDeleted: null,
  viewToBeRenamed: null,
  viewToBeEdited: null,
  isViewChanged: false,

  setShowViewsDrawer: unimplemented,
  setShowCreateViewModal: unimplemented,
  setViewToBeDeleted: unimplemented,
  setViewToBeRenamed: unimplemented,
  setViewToBeEdited: unimplemented,
  setSelectedView: unimplemented,
  setIsViewChanged: unimplemented,
};

const reducer = produce((draft: IViewsContext, action: Action) => {
  const { type, payload } = action;
  switch (type) {
    case VIEWS_CONTEXT_KEYS.SHOW_VIEWS_DRAWER:
      draft.showViewsDrawer = payload as boolean;
      break;
    case VIEWS_CONTEXT_KEYS.SHOW_CREATE_VIEW_MODAL:
      draft.showCreateViewModal = payload as boolean;
      break;

    case VIEWS_CONTEXT_KEYS.VIEW_TO_BE_DELETED:
      draft.viewToBeDeleted = payload as string | null;
      break;

    case VIEWS_CONTEXT_KEYS.VIEW_TO_BE_RENAMED:
      draft.viewToBeRenamed = payload as string | null;
      break;

    case VIEWS_CONTEXT_KEYS.VIEW_TO_BE_EDITED:
      draft.viewToBeEdited = payload as string | null;
      break;

    case VIEWS_CONTEXT_KEYS.SELECTED_VIEW:
      draft.selectedView = payload as string | null;
      if (!payload) {
        draft.isViewChanged = false;
      }
      break;

    case VIEWS_CONTEXT_KEYS.IS_VIEW_CHANGED:
      draft.isViewChanged = payload as boolean;
      break;

    default:
      break;
  }
}, initialState);

export const ViewsContext = createContext<IViewsContext>(initialState);

export const ViewsProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { selectedView } = state;
  const { getViewById } = useViews();
  const { queryToken } = useInsightsParams();
  const { garmParams } = useGarmParams();
  const { iabParams } = useIABParams();
  const { appleParams } = useAppleParams();
  const { groupParams } = useGroupParams();

  const {
    [INSIGHTS_QUERY_KEY.include_named_entities]: includedNamedEntities,
    [INSIGHTS_QUERY_KEY.exclude_named_entities]: excludedNamedEntities,
  } = groupParams;

  useEffect(() => {
    if (!selectedView) return;

    const view = getViewById(selectedView);
    if (!view) return;

    const filtersObject = JSON.parse(view.filters);
    const query = filtersObject?.query;
    setIsViewChanged(query && queryToken !== objectToToken(query));
  }, [
    garmParams,
    iabParams,
    appleParams,
    selectedView,
    includedNamedEntities,
    excludedNamedEntities,
  ]);

  function setShowViewsDrawer(value: boolean | ((prevState: boolean) => boolean)) {
    if (typeof value === 'function') {
      value = value(state.showViewsDrawer);
    }
    dispatch({ type: VIEWS_CONTEXT_KEYS.SHOW_VIEWS_DRAWER, payload: value });
  }

  function setShowCreateViewModal(value: boolean) {
    dispatch({ type: VIEWS_CONTEXT_KEYS.SHOW_CREATE_VIEW_MODAL, payload: value });
  }

  function setViewToBeDeleted(value: string | null) {
    dispatch({ type: VIEWS_CONTEXT_KEYS.VIEW_TO_BE_DELETED, payload: value });
  }

  function setViewToBeRenamed(value: string | null) {
    dispatch({ type: VIEWS_CONTEXT_KEYS.VIEW_TO_BE_RENAMED, payload: value });
  }

  function setViewToBeEdited(value: string | null) {
    dispatch({ type: VIEWS_CONTEXT_KEYS.VIEW_TO_BE_EDITED, payload: value });
  }

  function setSelectedView(value: string | null) {
    dispatch({ type: VIEWS_CONTEXT_KEYS.SELECTED_VIEW, payload: value });
  }

  function setIsViewChanged(value: boolean) {
    dispatch({ type: VIEWS_CONTEXT_KEYS.IS_VIEW_CHANGED, payload: value });
  }

  const value = {
    ...state,
    setShowViewsDrawer,
    setSelectedView,
    setShowCreateViewModal,
    setViewToBeDeleted,
    setViewToBeRenamed,
    setViewToBeEdited,
    setIsViewChanged,
  };

  return <ViewsContext.Provider value={value}>{children}</ViewsContext.Provider>;
};

export const useViewsContext = () => {
  return useContext(ViewsContext);
};
